<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import { readinessModule } from '@/store/readiness/module'
import { userModule } from '@/store/user/module'
import { analyticsModule } from '@/store/analytics/module'
import { toastModule } from '@/store/toast/module'
import { screenModule } from '@/store/screen/module'

const route = useRoute()

const {
    Icon,
    Radio: PocketRadio,
    CheckboxOption,
    Textarea: PocketTextarea,
    Button: PocketButton,
} = UIKit

const { isPasserInsightReport } = defineProps<{
    isPasserInsightReport?: boolean
}>()

const emit = defineEmits<{
    'close': []
}>()

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const isMobile = computed(() => {
    return screenModule.getters.getBreakpoint() === 'black-bear'
})

const confidenceOptions = [{
    value: 'Yes',
    label: 'Yes',
}, {
    value: 'Somewhat',
    label: 'Somewhat',
}, {
    value: 'No',
    label: 'No',
}, {
    value: 'Unsure',
    label: 'Unsure',
}]
const selectedConfidenceOption = ref<{
    value: string
    label: string
} | null>(null)

const feelingOptions = ref([{
    value: 'Motivated',
    enabled: false,
}, {
    value: 'Encouraged',
    enabled: false,
}, {
    value: 'Neutral',
    enabled: false,
}, {
    value: 'Confused',
    enabled: false,
}, {
    value: 'Discouraged',
    enabled: false,
}])

const readinessReflectionResponse = ref('')

// Enable the submit button if any survey question has been answered
const isSubmitEnabled = computed(() => {
    if (selectedConfidenceOption.value || feelingOptions.value.some(opt => opt.enabled)
        || readinessReflectionResponse.value) {
        return true
    }

    return false
})

const submitReadinessReportSurvey = () => {
    const passerInsightsSurveyAssessReadiness = selectedConfidenceOption.value?.value
    const passerInsightsSurveyFeeling = feelingOptions.value
        .filter(feelingOption => feelingOption.enabled)
        .map(feelingOption => feelingOption.value)
    const passerInsightsSurveyBehavior = readinessReflectionResponse.value
    const amplitudeReadinessProps = readinessModule.getters.getAmplitudeReadinessProps(route)
    analyticsModule.actions.amplitudeTrack('Passer Insights Report Survey Submitted', {
        ...amplitudeReadinessProps,
        passerInsightsSurveyAssessReadiness,
        passerInsightsSurveyFeeling,
        passerInsightsSurveyBehavior,
    })

    toastModule.actions.displayToast({
        title: 'Thanks for your feedback!',
    })

    emit('close')
}
</script>

<template>
    <div v-dark class="readiness-report-survey">
        <Icon class="readiness-report-survey__icon" type="readiness" />
        <div class="readiness-report-survey__title">
            Give Feedback
        </div>
        <div class="readiness-report-survey__divider" v-dark />
        <PocketRadio
            v-model="selectedConfidenceOption"
            :data="confidenceOptions"
            :is-dark-mode="isDarkMode"
            label="Did this report help you assess how ready you are to take your exam?"
            class="readiness-report-survey__confidence-radio"
        />
        <div class="readiness-report-survey__feeling-checkboxes">
            <div v-dark class="readiness-report-survey__feeling-label">
                How did seeing this report make you feel?
            </div>
            <CheckboxOption
                v-for="option in feelingOptions"
                :key="option.value"
                v-model="option.enabled"
                class="readiness-report-survey__feeling-checkbox-option"
                :label="option.value"
                :is-dark-mode="isDarkMode"
            />
        </div>
        <div class="readiness-report-survey__text-area-label" v-dark>
            What's one thing we could improve about how your {{
                isPasserInsightReport ? 'Passer Insights' : 'Readiness'
            }} Report is shown or explained?
        </div>
        <PocketTextarea
            v-model="readinessReflectionResponse"
            class="readiness-report-survey__textarea-field"
            :is-dark-mode="isDarkMode"
            height="62px"
            :key="`${isMobile}`"
            :width="isMobile ? '275px' : '408px'"
        />
        <div class="readiness-report-survey__buttons">
            <PocketButton
                :is-dark-mode="isDarkMode"
                type="secondary"
                @click="emit('close')"
            >
                Cancel
            </PocketButton>
            <PocketButton
                :is-dark-mode="isDarkMode"
                :disabled="!isSubmitEnabled"
                @click="submitReadinessReportSurvey"
            >
                Submit
            </PocketButton>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.readiness-report-survey {
    background-color: $white;
    border-radius: 12px;
    width: 472px;
    padding: 24px 32px 32px 32px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;

    @include breakpoint(black-bear) {
        padding: 24px 24px 32px 24px;
    }

    &--dark {
        background-color: $brand-black;
    }

    &__title {
        font-size: 18px;
        font-weight: 600;
        line-height: 25px;
        letter-spacing: 0.3px;
        margin: 6px 0;
    }

    &__divider, &__divider-bottom {
        width: 100%;
        background-color: $fog;
        height: 1px;
        margin-top: 24px;

        &--dark {
            background-color: $ash;
        }
    }

    &__divider-bottom {
        margin-top: 0;
        margin-bottom: 24px;
    }

    &__confidence-radio {
        margin-top: 30px;
        margin-bottom: 24px;
        margin-right: 8px;
    }

    &__confidence-radio :deep(.uikit-radio__label),
    &__feeling-label,
    &__textarea-field :deep(.uikit-textarea__label) {
        color: $brand-black;
        margin-left: 12px;
        font-size: 15px;
        font-weight: 600;
        line-height: 20px;

        @include breakpoint(black-bear) {
            padding-right: 0;
        }

        &.uikit-radio__label--dark,
        &.uikit-textarea__label--dark {
            color: $fog;
        }
    }

    &__confidence-radio :deep(.uikit-radio__label),
    &__feeling-label {
        margin-bottom: 10px;
    }

    &__confidence-radio :deep(.uikit-radio__list),
    &__feeling-checkbox-option {
        margin-left: 18px;
    }

    &__confidence-radio :deep(.uikit-radio__item--dark::before) {
        background-color: $pickled-bluewood;
    }

    &__feeling-label {
        &--dark {
            color: $fog;
        }
    }

    &__feeling-checkboxes {
        width: 100%;
        margin-bottom: 24px;
    }

    &__textarea-field :deep(.uikit-textarea__textarea) {
        height: 62px;
        display: block;
        resize: vertical;

        @include breakpoint(black-bear) {
            height: 72px;
        }
    }

    &__buttons {
        display: flex;
        align-items: center;
        width: 220px;
        margin-top: 30px;
        gap: 8px;
    }

    &__text-area-label {
        font-size: 16px;
        line-height: 22px;
        font-weight: 600;
        margin-bottom: 9px;
        margin-left: 12px;

        &--dark {
            color: $fog;
        }
    }

    &__text-area-sub-label {
        font-size: 15px;
        margin-bottom: 8px;

        &--dark {
            color: $fog;
        }
    }
}
</style>
